import type { CSSProperties } from 'react'
import { Link } from 'react-router-dom'
import type { ISODate } from '@app/shared'

import type { JournalMonth, JournalStats } from '../../lib/data'
import { useJournalEntry } from '../../lib/data'
import { ChevronDown, History, Mic } from './icons'
import { Heatmap, HeatmapSkeleton } from './Heatmap'
import { formatLongDateWithYear, oneYearBefore } from './util'

// ============================================================================
// THE JOURNAL INDEX'S PANELS — docs/design/handoff/"LifeOS Journal.dc.html", 5a.
//
// EVERY NUMBER ON THIS SCREEN IS READ BACK FROM THE DATA CONTRACT. None of it is
// typed into the markup: the mockup says "128 entries · 41,200 words · 322 avg"
// and the demo dataset was built to actually contain that, rather than the string
// being hard-coded over a dataset that contains twelve days.
// ============================================================================

// ── The stats row + heatmap ─────────────────────────────────────────────────

export function StatsPanel({
  stats,
  error,
  isPending,
}: {
  stats?: JournalStats
  error: unknown
  isPending: boolean
}) {
  return (
    <section className="flex flex-none items-center gap-[30px] rounded-context border border-line bg-surface px-6 py-5 shadow-card">
      {error ? (
        // A stats panel that renders zeroes claims "you have never written anything", which is a
        // far worse failure than an empty panel. Name the consequence, name what still works.
        //
        // NO LONGER A REFUSAL. These numbers are live (V6, GET /api/journal/stats); reaching this
        // branch now means a read that FAILED, which is a different sentence from "not built yet"
        // and has to read as one — the owner's next move depends on which it is.
        <p className="text-body-sm text-microcopy text-pretty">
          Totals, the streak and the writing heatmap could not be read —{' '}
          {error instanceof Error ? error.message : 'the archive read failed.'} The days below and
          every entry itself are unaffected.
        </p>
      ) : isPending || !stats ? (
        <>
          <div className="flex flex-none gap-[26px]" aria-hidden>
            <StatSkeleton />
            <div className="w-px self-stretch bg-fill" />
            <StatSkeleton />
          </div>
          <HeatmapSkeleton />
        </>
      ) : (
        <>
          <div className="flex flex-none gap-[26px]">
            <Stat
              value={stats.entryCount}
              unit="entries"
              sub={`${stats.wordCount.toLocaleString('en-US')} words · ${stats.averageWords} avg`}
            />
            <div className="w-px self-stretch bg-fill" aria-hidden />
            <Stat
              value={stats.currentStreak}
              unit="day streak"
              sub={`best ${stats.longestStreak} · ${stats.entriesThisWeek} entries this week`}
              tone="success"
            />
          </div>
          <Heatmap days={stats.heatmap} />
        </>
      )}
    </section>
  )
}

function Stat({
  value,
  unit,
  sub,
  tone,
}: {
  value: number
  unit: string
  sub: string
  tone?: 'success'
}) {
  return (
    <div>
      <div className="flex items-baseline gap-[7px]">
        <span
          className={`text-[40px] font-light leading-none tracking-[-0.03em] ${tone === 'success' ? 'text-success' : ''}`}
        >
          {value}
        </span>
        <span
          className={`text-[12px] font-medium leading-none ${tone === 'success' ? 'text-success-dot' : 'text-muted'}`}
        >
          {unit}
        </span>
      </div>
      <div className="mt-2 text-[11.5px] leading-none text-microcopy">{sub}</div>
    </div>
  )
}

function StatSkeleton() {
  return (
    <div>
      <div className="h-[32px] w-[74px] rounded-[6px] bg-fill" />
      <div className="mt-[10px] h-[9px] w-[128px] rounded-full bg-line-3" />
    </div>
  )
}

// ── The filter row ──────────────────────────────────────────────────────────

export interface Filters {
  hasVoice: boolean
  tags: string[]
  moodAtLeast: number | null
}

export const NO_FILTERS: Filters = { hasVoice: false, tags: [], moodAtLeast: null }

export const anyFilterOn = (f: Filters) =>
  f.hasVoice || f.tags.length > 0 || f.moodAtLeast !== null

/** The mood chip filters to 3-and-up — the range the mockup draws its dots at. */
export const MOOD_FLOOR = 3

export function FilterRow({
  stats,
  filters,
  onChange,
  month,
  onMonth,
  showing,
}: {
  stats?: JournalStats
  filters: Filters
  onChange: (next: Filters) => void
  month: string
  onMonth: (month: string) => void
  showing: string
}) {
  // Two tag chips, as the mockup draws. Which two is not a design choice — it is whichever two
  // the owner actually uses most, read off the data.
  //
  // Plus any tag that is CURRENTLY FILTERING, even when it is not in the top two: arriving from
  // the day page's "See all #neuc61" with no #neuc61 chip on screen would leave a filter running
  // with no visible way to turn it off.
  const top = stats?.tags.slice(0, 2) ?? []
  const pinned = filters.tags
    .filter((tag) => !top.some((t) => t.tag === tag))
    .map((tag) => ({ tag, count: stats?.tags.find((t) => t.tag === tag)?.count ?? 0 }))
  const topTags = [...top, ...pinned]

  return (
    <div className="flex flex-none flex-wrap items-center gap-[9px]">
      <Pill
        active={filters.hasVoice}
        onClick={() => onChange({ ...filters, hasVoice: !filters.hasVoice })}
      >
        <Mic size={13} strokeWidth={1.8} className={filters.hasVoice ? 'text-surface' : 'text-ink-2'} />
        <span className="text-[11.5px] font-semibold leading-none">Has voice</span>
        <Count active={filters.hasVoice}>{stats ? stats.withVoice : '—'}</Count>
      </Pill>

      {topTags.map(({ tag, count }) => {
        const on = filters.tags.includes(tag)
        return (
          <Pill
            key={tag}
            active={on}
            onClick={() =>
              onChange({
                ...filters,
                tags: on ? filters.tags.filter((t) => t !== tag) : [...filters.tags, tag],
              })
            }
          >
            <span className="text-[11.5px] font-medium leading-none">#{tag}</span>
            <Count active={on}>{count}</Count>
          </Pill>
        )
      })}

      <Pill
        active={filters.moodAtLeast !== null}
        onClick={() =>
          onChange({ ...filters, moodAtLeast: filters.moodAtLeast === null ? MOOD_FLOOR : null })
        }
      >
        <span
          className={`text-[11.5px] font-medium leading-none ${filters.moodAtLeast !== null ? '' : 'text-muted'}`}
        >
          Mood
        </span>
        {/* The dots draw the RANGE, not a value: 1 and 2 hollow, 3 to 5 filled. That is what the
            chip filters to, and it reads without a legend. */}
        <span className="flex items-center gap-1" aria-hidden>
          {[1, 2, 3, 4, 5].map((n) => (
            <span
              key={n}
              className={[
                'size-[8px] rounded-full',
                n >= MOOD_FLOOR ? 'bg-faint' : 'box-border border border-line-2',
              ].join(' ')}
            />
          ))}
        </span>
        <Count active={filters.moodAtLeast !== null}>{MOOD_FLOOR}–5</Count>
      </Pill>

      <MonthChip stats={stats} month={month} onMonth={onMonth} />

      <span className="ml-auto text-[11.5px] font-medium leading-none text-muted">{showing}</span>
    </div>
  )
}

/**
 * The month chip is a real `<select>` wearing the pill. A custom popover would have to
 * re-implement type-ahead, escape, and the focus trap that a native select already has, and this
 * codebase has no popover primitive to borrow one from yet.
 */
function MonthChip({
  stats,
  month,
  onMonth,
}: {
  stats?: JournalStats
  month: string
  onMonth: (month: string) => void
}) {
  const months = stats?.months ?? []
  const active = months.find((m) => m.month === month)
  return (
    <span
      className="mo-ink relative flex items-center gap-2 rounded-pill border border-line bg-surface px-3 py-[7px]"
      style={{ '--mo-rule': 'var(--color-journal)' } as CSSProperties}
    >
      <span className="text-[11.5px] font-medium leading-none text-ink-2">
        {active ? `${monthLabelLong(active)} ${active.month.slice(0, 4)}` : '—'}
      </span>
      <ChevronDown size={12} className="text-microcopy" />
      <select
        value={month}
        aria-label="Month"
        onChange={(e) => onMonth(e.target.value)}
        className="absolute inset-0 cursor-pointer opacity-0"
      >
        {months.map((m) => (
          <option key={m.month} value={m.month}>
            {monthLabelLong(m)} {m.month.slice(0, 4)}
          </option>
        ))}
      </select>
    </span>
  )
}

const MONTHS_LONG = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
]
const monthLabelLong = (m: JournalMonth) => MONTHS_LONG[Number(m.month.slice(5, 7)) - 1]!

function Pill({
  active,
  onClick,
  children,
}: {
  active: boolean
  onClick: () => void
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      className={[
        'flex items-center gap-[7px] rounded-pill px-3 py-[7px] transition-colors',
        'duration-[var(--dur-fast)] ease-[var(--ease-out)]',
        active
          ? 'bg-ink text-surface'
          : 'border border-line bg-surface text-ink-2 hover:bg-surface-4',
      ].join(' ')}
    >
      {children}
    </button>
  )
}

function Count({ active, children }: { active: boolean; children: React.ReactNode }) {
  return (
    <span
      className={`text-[10.5px] font-medium leading-none ${active ? 'text-muted' : 'text-microcopy'}`}
    >
      {children}
    </span>
  )
}

// ── The right column ────────────────────────────────────────────────────────

/**
 * ON THIS DAY. The one panel that keeps working in remote mode — it reads a single date through
 * GET /api/journal/:date, which has been live since V0.
 *
 * "Nothing a year ago" is a real answer and says so; it is not the same as a failed read, which
 * says something else entirely.
 */
export function OnThisDay({ from }: { from: ISODate }) {
  const date = oneYearBefore(from)
  const { data: entry, isPending, error } = useJournalEntry(date)

  return (
    <section className="flex flex-none flex-col gap-[11px] rounded-context border border-line bg-surface px-[18px] py-4 shadow-card">
      <header className="flex items-center gap-[9px]">
        <History size={17} strokeWidth={1.7} className="text-journal" />
        <h2 className="text-[12.5px] font-semibold leading-none">On this day</h2>
        <span className="ml-auto text-[10.5px] font-medium leading-none text-microcopy">
          1 year ago
        </span>
      </header>

      {isPending ? (
        <div className="flex flex-col gap-[9px]">
          <span className="sr-only">Loading…</span>
          <div className="h-[9px] w-[150px] rounded-full bg-fill" aria-hidden />
          <div className="h-[9px] w-full rounded-full bg-line-3" aria-hidden />
          <div className="h-[9px] w-[80%] rounded-full bg-line-3" aria-hidden />
        </div>
      ) : error ? (
        <p className="text-[11.5px] leading-[1.5] text-microcopy text-pretty">
          {error instanceof Error ? error.message : 'That day could not be read.'}
        </p>
      ) : !entry ? (
        <p className="text-[12.5px] leading-[1.6] text-ink-3 text-pretty">
          Nothing was written on {formatLongDateWithYear(date)}.
        </p>
      ) : (
        <>
          <div className="text-[12px] font-semibold leading-none text-ink-2">
            {formatLongDateWithYear(entry.date)}
          </div>
          <p className="line-clamp-5 text-[12.5px] leading-[1.6] text-ink-3 text-pretty">
            {entry.text}
          </p>
          <div className="flex items-center gap-2">
            <span className="rounded-pill border border-line-3 bg-surface-4 px-[9px] py-1 text-[10.5px] font-medium leading-none text-ink-2">
              {entry.wordCount} words
            </span>
            <Link
              to={`/journal/${entry.date}`}
              className="text-[11px] font-medium leading-none text-success-dot transition-colors duration-[var(--dur-state)] ease-[var(--ease-ink)] hover:text-success"
            >
              Read the day →
            </Link>
          </div>
        </>
      )}
    </section>
  )
}

export function MonthPicker({
  stats,
  month,
  onMonth,
}: {
  stats?: JournalStats
  month: string
  onMonth: (month: string) => void
}) {
  const active = stats?.months.find((m) => m.month === month)
  return (
    <section className="flex flex-none flex-col gap-[11px] rounded-context border border-line bg-surface px-[18px] py-4 shadow-card">
      <h2 className="text-[12.5px] font-semibold leading-none">Jump to a month</h2>
      <div className="grid grid-cols-3 gap-[6px]">
        {(stats?.months ?? []).map((m) => (
          <button
            key={m.month}
            type="button"
            aria-pressed={m.month === month}
            onClick={() => onMonth(m.month)}
            className={[
              'rounded-[8px] py-[7px] text-center text-[11px] leading-none transition-colors',
              'duration-[var(--dur-fast)] ease-[var(--ease-out)]',
              m.month === month
                ? 'bg-ink font-semibold text-surface'
                : 'bg-surface-4 font-medium text-ink-2 hover:bg-fill',
            ].join(' ')}
          >
            {m.label}
          </button>
        ))}
      </div>
      {active && (
        // "of 16 days" for the current month, not "of 30": the rest of September has not happened,
        // and counting unhappened days as unwritten is a reproach the data does not support.
        <p className="text-[11.5px] leading-[1.5] text-microcopy text-pretty">
          {monthLabelLong(active)}: {active.written} of {active.days} days written.
          {active.longestGap > 0 &&
            ` Longest gap ${active.longestGap} ${active.longestGap === 1 ? 'day' : 'days'}.`}
        </p>
      )}
    </section>
  )
}

/**
 * THE ONE OBSERVATION ON THE SCREEN, and it is an average and nothing more. The mockup's version
 * carries a cause ("right after discussion section"); this one does not, because the data cannot
 * supply one and the product does not invent them (CLAUDE.md → "Mirror, not adviser").
 */
export function QuietPattern({ stats }: { stats?: JournalStats }) {
  if (!stats?.quietPattern) return null
  const { longestWeekday, shortestWeekday } = stats.quietPattern
  return (
    <section className="flex flex-none flex-col gap-[9px] rounded-context border border-line-3 bg-surface-4 px-[18px] py-4">
      <h2 className="text-[10px] font-semibold uppercase leading-none tracking-[0.08em] text-microcopy">
        Quiet pattern
      </h2>
      <p className="text-[12.5px] leading-[1.6] text-ink-3 text-pretty">
        Your longest entries land on {longestWeekday}s. Your shortest on {shortestWeekday}s.
      </p>
    </section>
  )
}
